import {
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  GripHorizontalIcon,
  Loader2Icon,
  Maximize2Icon,
  MinusIcon,
  PictureInPictureIcon,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  collapseDocument,
  openFullscreen,
  selectExpandedDoc,
  unpinDocument,
} from '@/services/slices/pinnedDocsSlice';

const POSITION_STORAGE_KEY = 'pinned-docs-mini-panel-position';
const MIN_VISIBLE_WIDTH = 100; // Minimum width that must remain visible
const MIN_VISIBLE_HEIGHT = 100; // Minimum height that must remain visible

// Get panel dimensions based on screen width
const getPanelDimensions = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth < 1600) {
    return { width: 500, height: 600 };
  }
  return { width: 600, height: 700 };
};

// Get default position (centered horizontally, above tab bar)
const getDefaultPosition = (dimensions: { width: number; height: number }) => {
  return {
    x: Math.max(16, (window.innerWidth - dimensions.width) / 2),
    y: Math.max(16, window.innerHeight - dimensions.height - 48),
  };
};

// Load position from localStorage
const loadPosition = (): { x: number; y: number } | null => {
  try {
    const stored = localStorage.getItem(POSITION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        return parsed;
      }
    }
  } catch {
    // Silent fail
  }
  return null;
};

// Save position to localStorage
const savePosition = (position: { x: number; y: number }) => {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Silent fail
  }
};

// Clamp position to keep panel within viewport bounds
const clampPosition = (
  x: number,
  y: number,
  width: number,
): { x: number; y: number } => {
  const minX = -width + MIN_VISIBLE_WIDTH;
  const maxX = window.innerWidth - MIN_VISIBLE_WIDTH;
  const minY = 0;
  const maxY = window.innerHeight - MIN_VISIBLE_HEIGHT;

  return {
    x: Math.max(minX, Math.min(maxX, x)),
    y: Math.max(minY, Math.min(maxY, y)),
  };
};

// Memoized iframe to prevent re-renders from resetting scroll position
const StableIframe = React.memo(
  ({
    src,
    title,
    isLoading,
    onLoad,
    onError,
  }: {
    src: string;
    title: string;
    isLoading: boolean;
    onLoad: () => void;
    onError: () => void;
  }) => (
    <iframe
      src={src}
      title={title}
      className={cn(
        'absolute inset-0 w-full h-full transition-opacity duration-300',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      allow="fullscreen"
      onLoad={onLoad}
      onError={onError}
    />
  ),
);
StableIframe.displayName = 'StableIframe';

// Memoized image to prevent unnecessary re-renders
const StableImage = React.memo(
  ({
    src,
    alt,
    isLoading,
    onLoad,
    onError,
  }: {
    src: string;
    alt: string;
    isLoading: boolean;
    onLoad: () => void;
    onError: () => void;
  }) => (
    <img
      src={src}
      alt={alt}
      className={cn(
        'absolute inset-0 w-full h-full object-contain transition-opacity duration-300',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      onLoad={onLoad}
      onError={onError}
    />
  ),
);
StableImage.displayName = 'StableImage';

export const MiniPreviewPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const expandedDoc = useSelector(selectExpandedDoc);
  const panelRef = useRef<HTMLDivElement>(null);

  const [dimensions, setDimensions] = useState(getPanelDimensions);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const dims = getPanelDimensions();
    const saved = loadPosition();
    if (saved) {
      return clampPosition(saved.x, saved.y, dims.width);
    }
    return getDefaultPosition(dims);
  });
  const dragOffset = useRef({ x: 0, y: 0 });

  // Update dimensions and re-clamp position on resize
  useEffect(() => {
    const handleResize = () => {
      const newDimensions = getPanelDimensions();
      setDimensions(newDimensions);
      setPosition((prev) => clampPosition(prev.x, prev.y, newDimensions.width));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stabilize the download URL so that any Redux state changes
  // don't cause the iframe to reload and reset scroll position.
  const stableDownloadUrl = useRef(expandedDoc?.downloadUrl);
  const prevDocId = useRef(expandedDoc?.id);
  if (expandedDoc?.id !== prevDocId.current) {
    stableDownloadUrl.current = expandedDoc?.downloadUrl;
    prevDocId.current = expandedDoc?.id;
  }

  // Reset loading state when doc changes
  useEffect(() => {
    if (expandedDoc) {
      setIsLoading(true);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedDoc?.id]);

  const handleIframeLoad = useCallback(() => setIsLoading(false), []);
  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setError('Failed to load preview');
  }, []);
  const handleImageLoad = useCallback(() => setIsLoading(false), []);
  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setError('Failed to load image');
  }, []);

  const handleMinimize = useCallback(() => {
    dispatch(collapseDocument());
  }, [dispatch]);

  // Handle escape key to minimize
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && expandedDoc) {
        handleMinimize();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedDoc, handleMinimize]);

  // Drag handlers
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      dragOffset.current = {
        x: clientX - position.x,
        y: clientY - position.y,
      };
    },
    [position],
  );

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const newX = clientX - dragOffset.current.x;
      const newY = clientY - dragOffset.current.y;

      const clamped = clampPosition(newX, newY, dimensions.width);
      setPosition(clamped);
    };

    const handleEnd = () => {
      setIsDragging(false);
      savePosition(position);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dimensions, position]);

  // Reset position to default
  const handleResetPosition = useCallback(() => {
    const defaultPos = getDefaultPosition(dimensions);
    setPosition(defaultPos);
    savePosition(defaultPos);
    toast.success('Panel position reset');
  }, [dimensions]);

  if (!expandedDoc) {
    return null;
  }

  const handleMaximize = () => {
    dispatch(openFullscreen(expandedDoc.id));
  };

  const handleClose = () => {
    dispatch(unpinDocument(expandedDoc.id));
  };

  const handleGoToDocument = () => {
    // Navigate to the matter's documents tab with blob_id param for deep linking
    // The BlobFocusHandler in DocumentExplorer will expand parent folders and select the file
    navigate(
      `/matters/${expandedDoc.matterId}/documents?blob_id=${expandedDoc.id}`,
    );
    dispatch(collapseDocument());
    toast.success(`Navigated to ${expandedDoc.matterName}`);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = expandedDoc.downloadUrl;
    a.download = expandedDoc.fileName;
    a.click();
  };

  const handleOpenInNewTab = () => {
    window.open(expandedDoc.downloadUrl, '_blank');
  };

  const mimeType = expandedDoc.fileType || 'application/octet-stream';
  const isPreviewable =
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.includes('text/');

  return (
    <div
      ref={panelRef}
      className={cn(
        'fixed bg-white rounded-lg shadow-2xl border border-zinc-200',
        'flex flex-col overflow-hidden',
        !isDragging && 'animate-in slide-in-from-bottom-4 duration-200',
        isDragging && 'cursor-grabbing select-none',
      )}
      style={{
        width: dimensions.width,
        height: dimensions.height,
        left: position.x,
        top: position.y,
        zIndex: isDragging ? 9999 : 50, // Higher z-index when dragging
      }}
    >
      {/* Drag handle bar */}
      <div
        role="button"
        aria-label="Drag handle - drag to reposition panel, double-click to reset"
        tabIndex={0}
        className={cn(
          'flex items-center justify-center h-5 bg-zinc-100 border-b border-zinc-200 cursor-grab shrink-0',
          'hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-inset',
          isDragging && 'cursor-grabbing bg-zinc-200',
        )}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onDoubleClick={handleResetPosition}
        onKeyDown={(e) => {
          // Allow keyboard repositioning with arrow keys
          const step = e.shiftKey ? 50 : 10;
          let newX = position.x;
          let newY = position.y;
          switch (e.key) {
            case 'ArrowLeft':
              newX -= step;
              break;
            case 'ArrowRight':
              newX += step;
              break;
            case 'ArrowUp':
              newY -= step;
              break;
            case 'ArrowDown':
              newY += step;
              break;
            case 'Home':
            case 'Escape':
              handleResetPosition();
              return;
            default:
              return;
          }
          e.preventDefault();
          const clamped = clampPosition(newX, newY, dimensions.width);
          setPosition(clamped);
          savePosition(clamped);
        }}
        title="Drag to move • Double-click to reset position • Arrow keys to nudge"
      >
        <GripHorizontalIcon className="h-3 w-3 text-zinc-400" />
      </div>

      {/* Header - compact */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b bg-zinc-50 shrink-0">
        <div className="flex-1 min-w-0 mr-1.5">
          <button
            type="button"
            onClick={handleGoToDocument}
            className="text-xs font-semibold text-zinc-900 truncate block max-w-full text-left underline decoration-dashed decoration-zinc-400 underline-offset-2 hover:text-violet-700 hover:decoration-violet-500 transition-colors cursor-pointer"
            title={`${expandedDoc.fileName} - Click to go to document`}
          >
            {expandedDoc.fileName}
          </button>
          <p
            className="text-[10px] text-zinc-500 truncate"
            title={expandedDoc.matterName}
          >
            {expandedDoc.matterName}
          </p>
        </div>

        {/* Window controls - Order: Unpin (left), Maximize, Minimize (right) */}
        <div className="flex items-center gap-0 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-5 w-5 p-0 text-zinc-500 hover:text-red-600 hover:bg-red-50"
              >
                <PictureInPictureIcon size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Put it Back
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMaximize}
                className="h-5 w-5 p-0 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-200"
              >
                <Maximize2Icon size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Fullscreen
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMinimize}
                className="h-5 w-5 p-0 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-200"
              >
                <MinusIcon size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Minimize
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Preview content */}
      <div className="flex-1 relative bg-zinc-100 overflow-hidden">
        {!isPreviewable ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6">
            <div className="bg-white rounded-full p-4 shadow-sm">
              <FileIcon className="h-10 w-10 text-zinc-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-zinc-700 mb-1">
                Preview not available
              </p>
              <p className="text-xs text-zinc-500 mb-4">
                This file type cannot be previewed
              </p>
              <Button
                variant="default"
                size="sm"
                onClick={handleDownload}
                className="h-8 text-xs"
              >
                <DownloadIcon className="h-3.5 w-3.5 mr-1.5" />
                Download
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Loading state */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-100 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2Icon className="h-6 w-6 animate-spin text-violet-600" />
                  <span className="text-xs text-zinc-500">
                    Loading preview...
                  </span>
                </div>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-100 z-10">
                <div className="text-center p-4">
                  <p className="text-sm text-red-600 mb-2">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenInNewTab}
                    className="text-xs"
                  >
                    <ExternalLinkIcon className="h-3.5 w-3.5 mr-1.5" />
                    Open in new tab
                  </Button>
                </div>
              </div>
            )}

            {/* Preview iframe/image - use stable URL to prevent reloads */}
            {mimeType.startsWith('image/') ? (
              <StableImage
                src={stableDownloadUrl.current || ''}
                alt={expandedDoc.fileName}
                isLoading={isLoading}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            ) : (
              <StableIframe
                src={stableDownloadUrl.current || ''}
                title={expandedDoc.fileName}
                isLoading={isLoading}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MiniPreviewPanel;
